import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import LandingNavbar from "./components/LandingNavbar";
import LandingFooter from "./components/LandingFooter";
import {
  computeNavbarFeatureFlags,
  getCleanedServices,
} from "./landingPageSectionsConfig";
import "./landing-theme.css";

interface ProductService {
  id: string;
  name: string;
  description: string | null;
  price: number | null;
  photos: any[] | null;
  workspace_id: string;
}

interface SubdomainConfig {
  subdomain: string;
  theme_config: any;
  seo_config: any;
  location: any;
  workspace: any;
}

interface NavigationFlags {
  hasPublishedPosts: boolean;
  hasReviews: boolean;
  hasBlogs: boolean;
  hasProductsServices: boolean;
  hasGallery: boolean;
  hasSocial: boolean;
}

interface ProductDetailPageProps {
  ssrProduct?: ProductService;
  ssrSubdomainConfig?: SubdomainConfig;
  ssrNavigationFlags?: NavigationFlags;
  subdomain?: string;
  productId?: string;
}

const ProductDetailPage = ({
  ssrProduct,
  ssrSubdomainConfig,
  ssrNavigationFlags,
  subdomain: subdomainProp,
  productId: productIdProp
}: ProductDetailPageProps = {}) => {
  const params = useParams<{ subdomain?: string; slug?: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<ProductService | null>(ssrProduct || null);
  const [subdomainConfig, setSubdomainConfig] = useState<SubdomainConfig | null>(ssrSubdomainConfig || null);
  const [loading, setLoading] = useState(!ssrProduct);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [hasPublishedPosts, setHasPublishedPosts] = useState(ssrNavigationFlags?.hasPublishedPosts || false);
  const [hasReviews, setHasReviews] = useState(ssrNavigationFlags?.hasReviews || false);
  const [hasBlogs, setHasBlogs] = useState(ssrNavigationFlags?.hasBlogs || false);
  const [hasProductsServices, setHasProductsServices] = useState(ssrNavigationFlags?.hasProductsServices || false);
  const [hasGallery, setHasGallery] = useState(ssrNavigationFlags?.hasGallery || false);
  const [hasSocial, setHasSocial] = useState(ssrNavigationFlags?.hasSocial || false);
  const [hasServices, setHasServices] = useState(false);
  const [hasAppointment, setHasAppointment] = useState(false);
  const [location, setLocation] = useState<any>(ssrSubdomainConfig?.location || null);
  const [workspace, setWorkspace] = useState<any>(ssrSubdomainConfig?.workspace || null);

  // Extract subdomain from URL params OR hostname
  const getSubdomain = () => {
    // First check if passed as prop (from SSR)
    if (subdomainProp) {
      return subdomainProp;
    }

    // Then check URL params (for /subdomain/:subdomain route)
    if (params.subdomain) {
      return params.subdomain;
    }

    // Finally check hostname (for subdomain.localhost or subdomain.my-domain.com)
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');

      // For subdomain.localhost or subdomain.my-domain.com
      if (parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www') {
        return parts[0];
      }
    }

    return null;
  };

  const subdomain = getSubdomain();
  const productId = productIdProp || params.slug;

  // Check if we're on an actual subdomain (subdomain.localhost) vs testing route (/subdomain/:subdomain)
  const isOnSubdomain = () => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      // subdomain.localhost or subdomain.my-domain.com
      return parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www';
    }
    return false;
  };

  const getBasePath = () => {
    return isOnSubdomain() ? '' : `/subdomain/${subdomain}`;
  };

  useEffect(() => {
    // If we have SSR data, don't fetch again
    if (ssrProduct && ssrSubdomainConfig) {
      console.log("[ProductDetailPage] Using SSR data, skipping client-side fetch");
      return;
    }

    // Otherwise, fetch data client-side
    fetchData();
  }, [subdomain, productId, ssrProduct, ssrSubdomainConfig]);

  // Compute navbar feature flags when location/workspace/subdomainConfig changes
  useEffect(() => {
    if (location && workspace && subdomainConfig) {
      const availabilityData = {
        location,
        workspace,
        hasReviews,
        hasPublishedPosts,
        hasBlogs,
        hasProductsServices,
        customizations: subdomainConfig.theme_config?.customizations || {},
        getCleanedServices,
      };

      const featureFlags = computeNavbarFeatureFlags(availabilityData);
      setHasServices(featureFlags.hasServices);
      setHasGallery(featureFlags.hasGallery);
      setHasSocial(featureFlags.hasSocial);
      setHasAppointment(featureFlags.hasAppointment);
    }
  }, [location, workspace, subdomainConfig, hasReviews, hasPublishedPosts, hasBlogs, hasProductsServices]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch subdomain config
      const { data: configData, error: configError } = await supabase
        .from("subdomain_configs")
        .select(`
          *,
          location:gmb_locations!location_id(*),
          workspace:workspaces!workspace_id(*)
        `)
        .eq("subdomain", subdomain)
        .eq("is_active", true)
        .single();

      if (configError || !configData) {
        throw new Error("Subdomain not found");
      }

      setSubdomainConfig(configData);

      // Store location and workspace for navbar
      setLocation(configData.location);
      setWorkspace(configData.workspace);

      // Fetch product
      const { data: productData, error: productError } = await supabase
        .from("products_services")
        .select("*")
        .eq("id", productId)
        .eq("workspace_id", configData.workspace_id)
        .single();

      if (productError || !productData) {
        throw new Error("Product not found");
      }

      setProduct(productData);

      // Check for published posts
      const { data: postLocations } = await supabase
        .from("gmb_post_locations")
        .select("post_id, metadata")
        .eq("location_id", configData.location_id)
        .eq("status", "published")
        .limit(6);

      if (postLocations && postLocations.length > 0) {
        const postIds = postLocations.map(p => p.post_id);
        const { data: postContent } = await supabase
          .from("gmb_post_content")
          .select("id, summary")
          .in("id", postIds);

        let postsWithContent = [];
        if (postContent && postContent.length > 0) {
          postsWithContent = postContent.filter(content =>
            content.summary && content.summary.trim().length > 0
          );
        } else {
          postsWithContent = postLocations.filter(loc => {
            const summary = loc.metadata?.summary || loc.metadata?.text || "";
            return summary && summary.trim().length > 0;
          });
        }
        setHasPublishedPosts(postsWithContent.length > 0);
      }

      // Check for reviews
      const { count: reviewCount } = await supabase
        .from("gmb_reviews")
        .select("*", { count: 'exact', head: true })
        .eq("location_id", configData.location_id);
      setHasReviews((reviewCount || 0) > 0);

      // Check for blogs
      const { data: blogLocations } = await supabase
        .from("blog_locations")
        .select("blog_id")
        .eq("location_id", configData.location_id);

      if (blogLocations && blogLocations.length > 0) {
        const blogIds = blogLocations.map(bl => bl.blog_id);
        const { count: blogCount } = await supabase
          .from("blogs")
          .select("*", { count: 'exact', head: true })
          .in("id", blogIds)
          .eq("status", "published");
        setHasBlogs((blogCount || 0) > 0);
      }

      // Check for products/services
      const { count: productCount } = await supabase
        .from("products_services")
        .select("*", { count: 'exact', head: true })
        .eq("workspace_id", configData.workspace_id);
      setHasProductsServices((productCount || 0) > 0);

      // Check for gallery photos
      const hasGalleryPhotos = (configData.location?.photos?.length || 0) > 0;
      setHasGallery(hasGalleryPhotos);

      // Check for social links
      const hasSocialLinks = !!(
        configData.workspace?.facebook_url ||
        configData.workspace?.instagram_url ||
        configData.workspace?.twitter_url ||
        configData.workspace?.linkedin_url ||
        configData.workspace?.youtube_url
      );
      setHasSocial(hasSocialLinks);
    } catch (err: any) {
      console.error("Error fetching data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (error || !product || !subdomainConfig) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Product Not Found
          </h1>
          <p className="text-gray-600 mb-4">
            The product you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate(`${getBasePath()}/`)}>
            Go Back Home
          </Button>
        </div>
      </div>
    );
  }

  const { theme_config } = subdomainConfig;

  // Build theme classes from theme_config
  const buildThemeClasses = () => {
    const classes = [`theme-${theme_config?.theme || 'light'}`];

    if (theme_config?.styles) {
      Object.values(theme_config.styles).forEach((styleValue) => {
        if (styleValue) {
          classes.push(styleValue as string);
        }
      });
    }

    return classes.join(' ');
  };

  const logoUrl = location?.media_items?.logo?.sourceUrl ||
                  location?.media_items?.logo?.thumbnailUrl ||
                  location?.media_items?.logo?.googleUrl ||
                  workspace?.logo_url;

  const businessInfo = {
    name: location?.name || workspace?.name,
    address: location?.formatted_address || location?.address,
    phone: location?.primary_phone,
    email: workspace?.email,
    hours: location?.business_hours || location?.regular_hours,
  };

  console.log("Product photos:", product.photos);
  const photosArray = Array.isArray(product.photos) ? product.photos : [];

  // Convert all photos to URL strings
  const allPhotoUrls = photosArray.map((photo: any) =>
    typeof photo === 'string' ? photo : (photo?.sourceUrl || photo?.url || photo?.googleUrl || photo?.thumbnailUrl)
  ).filter(Boolean);

  const selectedPhotoUrl = allPhotoUrls[selectedImageIndex];
  console.log("All photo URLs:", allPhotoUrls);
  console.log("Selected photo URL:", selectedPhotoUrl);

  return (
    <>
      <Helmet>
        <title>{`${product.name} - ${location?.name || workspace?.name}`}</title>
        <meta name="description" content={product.description || `View details for ${product.name}`} />
      </Helmet>

      <div className={`subdomain-landing-root ${buildThemeClasses()} min-h-screen flex flex-col`}>
        <LandingNavbar
          businessName={location?.name || workspace?.name}
          logoUrl={logoUrl}
          pages={[]}
          theme={theme_config}
          hasPublishedPosts={hasPublishedPosts}
          hasReviews={hasReviews}
          hasBlogs={hasBlogs}
          hasProductsServices={hasProductsServices}
          hasServices={hasServices}
          hasGallery={hasGallery}
          hasSocial={hasSocial}
          hasAppointment={hasAppointment}
          phoneNumber={location?.primary_phone}
          editMode={false}
          onTrackPhoneClick={undefined}
          location={location}
          workspace={workspace}
          getCleanedServices={getCleanedServices}
        />

        <main className="flex-1 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Button */}
            <Button
              variant="ghost"
              onClick={() => navigate(`${getBasePath()}/`)}
              className="mb-8"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Product Images */}
              <div className="space-y-4">
                {/* Main Image */}
                {selectedPhotoUrl && (
                  <div className="aspect-square w-full overflow-hidden rounded-lg bg-gray-200">
                    <img
                      src={selectedPhotoUrl}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Thumbnail Scroll */}
                {allPhotoUrls.length > 1 && (
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {allPhotoUrls.map((photoUrl: string, index: number) => (
                      <div
                        key={index}
                        onClick={() => setSelectedImageIndex(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden cursor-pointer transition-all ${
                          selectedImageIndex === index
                            ? 'ring-4 ring-offset-2'
                            : 'opacity-60 hover:opacity-100'
                        }`}
                        style={selectedImageIndex === index ? { borderColor: "var(--primary)" } : {}}
                      >
                        <img
                          src={photoUrl}
                          alt={`${product.name} thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="space-y-6">
                <div>
                  <h1
                    className="text-4xl font-bold mb-4"
                    style={{ color: "var(--primary)" }}
                  >
                    {product.name}
                  </h1>

                  {product.price && (
                    <div className="mb-6">
                      <span
                        className="text-3xl font-bold"
                        style={{ color: "var(--primary)" }}
                      >
                        ₹{product.price.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                {product.description && (
                  <div>
                    <h2 className="text-xl font-semibold mb-3 text-gray-900">
                      Description
                    </h2>
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {product.description}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

        <LandingFooter
          businessName={location?.name || workspace?.name}
          businessInfo={businessInfo}
          theme={theme_config}
          workspace={workspace}
          location={location}
        />
      </div>
    </>
  );
};

export default ProductDetailPage;
